import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, TrendingUp, Activity, Loader, RefreshCw } from 'lucide-react';
import superAdminService from '../../services/superadmin/superAdminService';
import { Admin } from '../../types/api.types';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Fetch all admins from API
  const fetchAdmins = async (showToast = false) => {
    // Check if token exists before making API call
    const token = localStorage.getItem('authToken');
    if (!token || !token.trim()) {
      console.warn('⚠️ No token found - cannot fetch admins');
      if (showToast) {
        toast.error('Authentication required. Please login again.');
      }
      setLoading(false);
      setRefreshing(false);
      // Redirect to login if no token
      if (!window.location.pathname.includes('/login')) {
        window.location.replace('/admin/login');
      }
      return;
    }

    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await superAdminService.getAllAdmins();
      setAdmins(data);
      if (showToast) {
        toast.success('Dashboard refreshed!');
      }
    } catch (error: any) {
      // Handle different error types
      if (error?.isAuthError || 
          error?.message?.includes('Authentication token not found') || 
          error?.response?.status === 401 ||
          error?.response?.data?.message?.toLowerCase().includes('no token') ||
          error?.response?.data?.message?.toLowerCase().includes('unauthorized')) {
        // Token missing or invalid - redirect to login
        console.warn('⚠️ Authentication error - redirecting to login');
        if (!window.location.pathname.includes('/login')) {
          // Clear auth data
          localStorage.removeItem('authToken');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userEmail');
          // Redirect immediately
          window.location.href = '/admin/login';
        }
        // Don't show error toast for auth errors
        return;
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to fetch dashboard data');
      }
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Calculate stats from real data
  const stats = {
    total: admins.length,
    active: admins.filter(a => a.is_active === 1).length,
    inactive: admins.filter(a => a.is_active === 0).length,
    thisMonth: admins.filter(a => {
      const createdDate = new Date(a.created_at);
      const now = new Date();
      return createdDate.getMonth() === now.getMonth() && 
             createdDate.getFullYear() === now.getFullYear();
    }).length,
  };

  // Get recent admins (last 5)
  const recentAdmins = [...admins]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get time ago
  const getTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Dashboard</h1>
          <p className="text-secondary-600">Overview of admin statistics and system health</p>
        </div>
        <button
          onClick={() => fetchAdmins(true)}
          disabled={refreshing}
          className="bg-primary-50 hover:bg-primary-100 text-primary-600 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards - Real Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Admins */}
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-primary-500">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-primary-100 p-3 rounded-lg">
              <Users className="text-primary-600" size={24} />
            </div>
            <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
              TOTAL
            </span>
          </div>
          <h3 className="text-3xl font-bold text-secondary-900 mb-1">{stats.total}</h3>
          <p className="text-sm text-secondary-600">Total Admins</p>
        </div>

        {/* Active Admins */}
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-success-500">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-success-100 p-3 rounded-lg">
              <UserCheck className="text-success-600" size={24} />
            </div>
            <span className="text-xs font-semibold text-success-600 bg-success-50 px-2 py-1 rounded-full">
              ACTIVE
            </span>
          </div>
          <h3 className="text-3xl font-bold text-secondary-900 mb-1">{stats.active}</h3>
          <p className="text-sm text-secondary-600">Active Admins</p>
        </div>

        {/* Inactive Admins */}
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-warning-500">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-warning-100 p-3 rounded-lg">
              <UserX className="text-warning-600" size={24} />
            </div>
            <span className="text-xs font-semibold text-warning-600 bg-warning-50 px-2 py-1 rounded-full">
              INACTIVE
            </span>
          </div>
          <h3 className="text-3xl font-bold text-secondary-900 mb-1">{stats.inactive}</h3>
          <p className="text-sm text-secondary-600">Inactive Admins</p>
        </div>

        {/* This Month */}
        <div className="bg-white rounded-xl p-6 shadow-soft border-l-4 border-info-500">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-info-100 p-3 rounded-lg">
              <TrendingUp className="text-info-600" size={24} />
            </div>
            <span className="text-xs font-semibold text-info-600 bg-info-50 px-2 py-1 rounded-full">
              THIS MONTH
            </span>
          </div>
          <h3 className="text-3xl font-bold text-secondary-900 mb-1">{stats.thisMonth}</h3>
          <p className="text-sm text-secondary-600">Added This Month</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Admins */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-secondary-900">Recent Admins</h3>
            <span className="text-xs font-semibold text-secondary-600 bg-secondary-100 px-3 py-1 rounded-full">
              LAST 5
            </span>
          </div>
          
          {recentAdmins.length === 0 ? (
            <div className="text-center py-8 text-secondary-600">
              No admins found
            </div>
          ) : (
            <div className="space-y-4">
              {recentAdmins.map((admin) => (
                <div key={admin.admin_id} className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {admin.full_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-secondary-900">{admin.full_name}</p>
                      <p className="text-sm text-secondary-600">{admin.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                      admin.is_active === 1 
                        ? 'bg-success-50 text-success-600' 
                        : 'bg-danger-50 text-danger-600'
                    }`}>
                      {admin.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                    <p className="text-xs text-secondary-500 mt-1">
                      {formatDate(admin.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* System Health & Quick Actions */}
        <div className="space-y-6">
          {/* System Health */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-secondary-900">System Health</h3>
              <Activity className="text-success-500" size={24} />
            </div>
            
            <div className="space-y-4">
              {/* Admin Status */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-secondary-600">Active Admin Rate</span>
                  <span className="text-sm font-semibold text-secondary-900">
                    {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-secondary-200 rounded-full h-2">
                  <div 
                    className="bg-success-500 h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${stats.total > 0 ? (stats.active / stats.total) * 100 : 0}%` 
                    }}
                  />
                </div>
              </div>

              {/* Database Status */}
              <div className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
                <span className="text-sm font-semibold text-secondary-700">Database</span>
                <span className="text-xs font-semibold text-success-600 flex items-center">
                  <span className="w-2 h-2 bg-success-500 rounded-full mr-2 animate-pulse" />
                  Connected
                </span>
              </div>

              {/* API Status */}
              <div className="flex items-center justify-between p-3 bg-success-50 rounded-lg">
                <span className="text-sm font-semibold text-secondary-700">API Server</span>
                <span className="text-xs font-semibold text-success-600 flex items-center">
                  <span className="w-2 h-2 bg-success-500 rounded-full mr-2 animate-pulse" />
                  Online
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-soft p-6 text-white">
            <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <a
                href="/super-admin/admin-management"
                className="flex items-center justify-between p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all hover:scale-105"
              >
                <span className="text-sm font-semibold">Manage Admins</span>
                <Users size={18} />
              </a>
              <a
                href="/super-admin/profile"
                className="flex items-center justify-between p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all hover:scale-105"
              >
                <span className="text-sm font-semibold">View Profile</span>
                <UserCheck size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
